import anime from 'animejs';
import { live } from '../data/live';
import { schedule } from '../data/schedule';

/**
 * The jukebox: background music for the break screen, the scrolling "artist - song"
 * ticker, and the chat TTS player.
 *
 * Lifted wholesale out of live.js, where it was roughly half the file and shared
 * nothing with the donation totals, the incentives or the schedule. It reads two things
 * from elsewhere — the frozen-inforoll playlist off the live payload, and the next run's
 * bgms off the schedule — and is otherwise self-contained.
 *
 * Entirely inert unless the page is opened with ?bgm=true, and the chat TTS half is
 * switched off outright by `disableChatAudio`.
 *
 * intro.wav and outro.wav are gone from the repo — 18 MB for a feature that has been
 * hard-disabled, and after the clinic videos went they were the largest thing in the
 * bundle. The two paths below are plain strings for the same reason the clinic ones are:
 * `new URL(…, import.meta.url)` is a build-time dependency and parcel fails on a missing
 * file. Restoring the feature means putting the files back under resources/chat/ *and*
 * switching these back to new URL(), or parcel will not fingerprint them and they 404.
 */
export const jukebox = {
    currentBgmIndex: 0,

    currentBgmScrollIndex: 0,
    maxBgmScrollIndex: 2,
    bgmScrollMaxLeft: 0,
    bgmScrollLeft: 0,
    bgmScrollElements: [],
    bgmCurrentTitle: '',
    scrollBgmInterval: null,

    scrollBgmLoop: () => {
        jukebox.bgmScrollElements = document.querySelectorAll('#media-player .media-title-text');
        jukebox.bgmScrollMaxLeft = jukebox.bgmScrollElements[jukebox.currentBgmScrollIndex].clientWidth;
        jukebox.bgmScrollElements.forEach((element, i) => {
            element.style.order = i;
        });
        jukebox.scrollBgmInterval = setInterval(function () {
            jukebox.bgmScrollElements.forEach((element) => {
                element.style.left = jukebox.bgmScrollLeft + 'px';
            });

            jukebox.bgmScrollLeft -= 1;
            if (jukebox.bgmScrollLeft <= jukebox.bgmScrollMaxLeft * -1) {
                jukebox.bgmScrollLeft = 0;
                jukebox.currentBgmScrollIndex++;
                if (jukebox.currentBgmScrollIndex > jukebox.maxBgmScrollIndex)
                    jukebox.currentBgmScrollIndex = 0;

                jukebox.bgmScrollElements.forEach((element) => {
                    let order = parseInt(element.style.order);
                    order--;
                    if (order < 0)
                        order += jukebox.bgmScrollElements.length;

                    element.style.order = order;
                });
                jukebox.bgmScrollMaxLeft = jukebox.bgmScrollElements[jukebox.currentBgmScrollIndex].clientWidth;
            }
        }, 30);
    },

    clearTtsFilename: () => {
        console.log("Clearing tts filename");
        try {
            fetch(live.chatDataEndpoint, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${live.offsetWriterToken}`,
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
            }).then(async (data) => {
                const responseCode = data.status;
                if (responseCode !== 204) {
                    console.error("Failed to clear tts filename");
                    return;
                }

                console.log("Cleared tts filename");
            });
        } catch (error) {
            console.error(error);
        }
    },

    canUseNewAudioChat: false,

    getChatAudio: () => {
        const filename = live.liveEventData?.live?.tts_filename;
        if (!filename)
            return null;

        return mainApiUrl + filename;
    },

    getNextRunBgms: () => {
        return schedule.getNextRun(0)?.submission?.bgms ?? [];
    },

    getNextRunBgm: () => {
        let bgms = [];
        if (live.getFreezeInforoll())
            bgms = live.getFreezeInforollMedia();
        else
            bgms = jukebox.getNextRunBgms();

        if (bgms.length == 0)
            return [];

        if (bgms.length <= jukebox.currentBgmIndex)
            jukebox.currentBgmIndex = 0;

        return bgms[jukebox.currentBgmIndex] ?? [];
    },

    playNextRunBgmWithDelay: () => {
        setTimeout(jukebox.playNextRunBgm, 6000);
    },

    setBgmMediaTitles: (nextRunBgm) => {
        const titleArray = [
            nextRunBgm?.artist || '',
            nextRunBgm?.song || '',
        ];
        jukebox.bgmCurrentTitle = titleArray.join(' - ');
        anime({
            targets: '#media-player .media-title-container',
            opacity: 0,
            duration: 2000,
            easing: 'linear',
            complete: () => {
                jukebox.bgmScrollLeft = 0;
                clearInterval(jukebox.scrollBgmInterval);
                document.querySelectorAll('#media-player .media-title-text')
                    .forEach(element => element.innerText = jukebox.bgmCurrentTitle);
                jukebox.scrollBgmLoop();
                anime({
                    targets: '#media-player .media-title-container',
                    opacity: 1,
                    duration: 2000,
                    easing: 'linear',
                });
            }
        });
    },

    playNextRunBgm: () => {
        let nextRunBgm = jukebox.getNextRunBgm();
        if (!nextRunBgm || nextRunBgm.length == 0)
            return;

        console.log("Next run BGM not empty, playing", nextRunBgm);
        jukebox.playBgm(nextRunBgm);

        jukebox.currentBgmIndex++;
    },

    maxChatAudio: 6,
    currentChatAudio: 0,
    disableChatAudio: true,

    queueNextChatAudio: () => {
        console.log("Playing next chat audio in 12s");
        setTimeout(jukebox.playChatAudio, 12000);
    },

    playChatAudio: (force = false, path = null) => {
        if (jukebox.disableChatAudio) {
            return;
        }

        if (!jukebox.canUseNewAudioChat && !force) {
            console.log("Cannot set new audio chat until flag is cleared.");
            jukebox.queueNextChatAudio();
            return;
        }

        const chatAudioUrl = path || jukebox.getChatAudio();
        if (!chatAudioUrl) {
            jukebox.queueNextChatAudio();
            return;
        }

        jukebox.canUseNewAudioChat = false;

        const audio = document.querySelector("#chat-audio audio");
        if (!force && jukebox.currentChatAudio >= jukebox.maxChatAudio) {
            jukebox.playOutroChatAudio();
            console.log("Disabling chat audio.")
            jukebox.disableChatAudio = true;
            return;
        }

        jukebox.clearTtsFilename();

        const sourceSelector = audio.querySelector("source");
        const bgmType = 'audio/wav';
        const bgmUrl = chatAudioUrl;
        sourceSelector.type = bgmType;
        sourceSelector.src = bgmUrl;
        console.log("Audio chat", bgmType, bgmUrl);

        audio.load();
        audio.volume = 0.19;
        jukebox.actualMaxBgmVolume = 0.01;
        anime({
            targets: jukebox.getBgmAudioElement(),
            duration: 2000,
            easing: 'linear',
            volume: jukebox.actualMaxBgmVolume,
        });
        audio.play();
        jukebox.currentChatAudio++;
        console.log("Current chat audio count:", jukebox.currentChatAudio);
    },

    playIntroChatAudio: () => {
        jukebox.playChatAudio(true, 'resources/chat/intro.wav');
    },

    playOutroChatAudio: () => {
        jukebox.playChatAudio(true, 'resources/chat/outro.wav');
    },

    getBgmAudioElement: () => {
        return document.querySelector("#media-player audio");
    },

    resetBgmVolume: () => {
        jukebox.actualMaxBgmVolume = jukebox.maxBgmVolume;
        anime({
            targets: jukebox.getBgmAudioElement(),
            duration: 2000,
            easing: 'linear',
            volume: jukebox.actualMaxBgmVolume,
        });
    },

    setChatAudioAsPlayable: () => {
        console.log("Can now play new audio chat.");
        jukebox.canUseNewAudioChat = true;
    },

    maxBgmVolume: 0.2,
    actualMaxBgmVolume: 0.2,
    playBgm: (bgm) => {
        jukebox.setBgmMediaTitles(bgm);

        const audio = jukebox.getBgmAudioElement();
        const sourceSelector = audio.querySelector("source");
        const bgmType = 'audio/mpeg';
        const bgmUrl = bgm.file_name;
        sourceSelector.type = bgmType;
        sourceSelector.src = bgmUrl;
        console.log("BGM", bgmType, bgmUrl);
        audio.load();
        audio.volume = 0;
        anime({
            targets: audio,
            duration: 500,
            easing: 'linear',
            volume: jukebox.actualMaxBgmVolume,
        });
        audio.play();
    },

};

/**
 * Attaches the audio players. Called from init after the first poll; does nothing
 * unless the page was opened with ?bgm=true.
 */
jukebox.setUp = () => {
    const bgm = new URLSearchParams(window.location.search).get('bgm') === 'true';
    if (!bgm) return;

    window.playBgm = true;
    console.log("BGM enabled, setting up event listeners and playing first BGM...");

    const audio = jukebox.getBgmAudioElement();
    audio.addEventListener("ended", jukebox.playNextRunBgmWithDelay, false);
    audio.volume = jukebox.actualMaxBgmVolume;
    jukebox.playNextRunBgmWithDelay();

    const chatAudio = document.querySelector("#chat-audio audio");
    chatAudio.addEventListener("ended", jukebox.resetBgmVolume, false);
    chatAudio.addEventListener("ended", jukebox.setChatAudioAsPlayable, false);
    chatAudio.addEventListener("ended", jukebox.queueNextChatAudio, false);
};

window.jukebox = jukebox;
